import Phaser from 'phaser';
import { DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, DEPTH, getYSortedDepth } from '../constants';

// ============================================
// Step 4: Rendering Foundation (2.5D Style)
// ============================================

// Render layer definitions - Updated for proper 2.5D Y-sorting
export const RENDER_LAYERS = {
  GROUND: 0,
  PATH: 10,
  SHADOWS: 25,
  // Y-sorted entities use DEPTH.Y_SORT_BASE to DEPTH.Y_SORT_MAX (100-999)
  Y_SORT_BASE: DEPTH.Y_SORT_BASE,
  // Ghost layer for see-through entities
  GHOST_LAYER: DEPTH.GHOST_LAYER,
  PROJECTILES: DEPTH.PROJECTILES,
  FX: 1150,
  UI_WORLD: 1180,   // World-space UI elements
  UI_SCREEN: DEPTH.UI_BACKGROUND, // Screen-space UI
} as const;

// Tile/Grid configuration
export interface GridConfig {
  tileWidth: number;
  tileHeight: number;
  mapWidthTiles: number;
  mapHeightTiles: number;
  originX: number;
  originY: number;
}

const DEFAULT_GRID_CONFIG: GridConfig = {
  tileWidth: 64,
  tileHeight: 64,
  mapWidthTiles: 20,
  mapHeightTiles: 14,
  originX: 0,
  originY: 0,
};

class RenderSystemManager {
  private scene: Phaser.Scene | null = null;
  private gridConfig: GridConfig = DEFAULT_GRID_CONFIG;

  // Layer containers
  private layers: Map<number, Phaser.GameObjects.Container> = new Map();

  // Camera reference
  private mainCamera: Phaser.Cameras.Scene2D.Camera | null = null;

  init(scene: Phaser.Scene, gridConfig?: Partial<GridConfig>): void {
    this.scene = scene;
    this.gridConfig = { ...DEFAULT_GRID_CONFIG, ...gridConfig };
    this.mainCamera = scene.cameras.main;

    // Setup camera bounds
    this.setupCamera();

    // Create layer containers
    this.createLayers();
  }

  private setupCamera(): void {
    if (!this.mainCamera || !this.scene) return;

    const worldWidth = this.gridConfig.mapWidthTiles * this.gridConfig.tileWidth;
    const worldHeight = this.gridConfig.mapHeightTiles * this.gridConfig.tileHeight;

    // Set camera bounds to map size
    this.mainCamera.setBounds(0, 0, worldWidth, worldHeight);

    // If map is smaller than screen, center it
    if (worldWidth < DESIGN_WIDTH || worldHeight < DESIGN_HEIGHT) {
      this.mainCamera.centerOn(worldWidth / 2, worldHeight / 2);
    }
  }

  private createLayers(): void {
    if (!this.scene) return;

    // Create containers for each layer
    Object.entries(RENDER_LAYERS).forEach(([name, depth]) => {
      const container = this.scene!.add.container(0, 0);
      container.setDepth(depth);
      container.setName(`Layer_${name}`);
      this.layers.set(depth, container);
    });
  }

  // ============================================
  // Layer Management
  // ============================================

  /**
   * Add a game object to a specific layer
   */
  addToLayer(object: Phaser.GameObjects.GameObject, layer: number): void {
    const container = this.layers.get(layer);
    if (container && 'add' in container) {
      container.add(object);
    } else {
      // Fallback: just set depth directly
      if ('setDepth' in object) {
        (object as unknown as Phaser.GameObjects.Components.Depth).setDepth(layer);
      }
    }
  }

  /**
   * Get a layer container
   */
  getLayer(layer: number): Phaser.GameObjects.Container | undefined {
    return this.layers.get(layer);
  }

  // ============================================
  // Depth Sorting (2.5D Effect)
  // ============================================

  /**
   * Calculate depth for a unit based on Y position
   * Higher Y = further down screen = rendered on top
   */
  calculateUnitDepth(y: number): number {
    const mapHeight = this.gridConfig.mapHeightTiles * this.gridConfig.tileHeight;
    return getYSortedDepth(y, mapHeight);
  }

  /**
   * Update an entity's depth based on its Y position
   */
  updateEntityDepth(entity: Phaser.GameObjects.Components.Depth & { y: number }): void {
    entity.setDepth(this.calculateUnitDepth(entity.y));
  }

  // ============================================
  // Grid / Tile Conversion
  // ============================================

  /**
   * Convert tile coordinates to world coordinates (center of tile)
   */
  tileToWorld(tileX: number, tileY: number): { x: number; y: number } {
    return {
      x: this.gridConfig.originX + (tileX + 0.5) * this.gridConfig.tileWidth,
      y: this.gridConfig.originY + (tileY + 0.5) * this.gridConfig.tileHeight,
    };
  }

  /**
   * Convert world coordinates to tile coordinates
   */
  worldToTile(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: Math.floor((worldX - this.gridConfig.originX) / this.gridConfig.tileWidth),
      y: Math.floor((worldY - this.gridConfig.originY) / this.gridConfig.tileHeight),
    };
  }

  /**
   * Check if tile coordinates are within map bounds
   */
  isValidTile(tileX: number, tileY: number): boolean {
    return (
      tileX >= 0 &&
      tileX < this.gridConfig.mapWidthTiles &&
      tileY >= 0 &&
      tileY < this.gridConfig.mapHeightTiles
    );
  }

  /**
   * Get grid configuration
   */
  getGridConfig(): GridConfig {
    return { ...this.gridConfig };
  }

  // ============================================
  // Camera Helpers
  // ============================================

  /**
   * Center camera on a world position
   */
  centerOn(x: number, y: number): void {
    this.mainCamera?.centerOn(x, y);
  }

  /**
   * Center camera on a tile position
   */
  centerOnTile(tileX: number, tileY: number): void {
    const world = this.tileToWorld(tileX, tileY);
    this.centerOn(world.x, world.y);
  }

  /**
   * Pan camera to a position over time
   */
  panTo(x: number, y: number, duration: number = 500): void {
    if (!this.mainCamera) return;
    this.mainCamera.pan(x, y, duration, 'Power2');
  }

  /**
   * Pan camera to tile position over time
   */
  panToTile(tileX: number, tileY: number, duration: number = 500): void {
    const world = this.tileToWorld(tileX, tileY);
    this.panTo(world.x, world.y, duration);
  }

  /**
   * Center on base position (typically right side of map)
   */
  centerOnBase(baseTileX: number, baseTileY: number): void {
    this.centerOnTile(baseTileX, baseTileY);
  }

  /**
   * Set camera zoom
   */
  setZoom(zoom: number): void {
    this.mainCamera?.setZoom(zoom);
  }

  /**
   * Get current camera viewport in world coordinates
   */
  getViewport(): { x: number; y: number; width: number; height: number } {
    if (!this.mainCamera) {
      return { x: 0, y: 0, width: DESIGN_WIDTH, height: DESIGN_HEIGHT };
    }
    return {
      x: this.mainCamera.worldView.x,
      y: this.mainCamera.worldView.y,
      width: this.mainCamera.worldView.width,
      height: this.mainCamera.worldView.height,
    };
  }

  // ============================================
  // Debug Rendering
  // ============================================

  /**
   * Draw debug grid
   */
  drawDebugGrid(graphics: Phaser.GameObjects.Graphics, alpha: number = 0.2): void {
    graphics.lineStyle(1, 0xffffff, alpha);

    for (let x = 0; x <= this.gridConfig.mapWidthTiles; x++) {
      const worldX = this.gridConfig.originX + x * this.gridConfig.tileWidth;
      graphics.lineBetween(
        worldX,
        this.gridConfig.originY,
        worldX,
        this.gridConfig.originY + this.gridConfig.mapHeightTiles * this.gridConfig.tileHeight
      );
    }

    for (let y = 0; y <= this.gridConfig.mapHeightTiles; y++) {
      const worldY = this.gridConfig.originY + y * this.gridConfig.tileHeight;
      graphics.lineBetween(
        this.gridConfig.originX,
        worldY,
        this.gridConfig.originX + this.gridConfig.mapWidthTiles * this.gridConfig.tileWidth,
        worldY
      );
    }
  }

  /**
   * Draw tile highlight
   */
  drawTileHighlight(
    graphics: Phaser.GameObjects.Graphics,
    tileX: number,
    tileY: number,
    color: number = COLORS.ACCENT,
    alpha: number = 0.3
  ): void {
    const world = this.tileToWorld(tileX, tileY);
    graphics.fillStyle(color, alpha);
    graphics.fillRect(
      world.x - this.gridConfig.tileWidth / 2,
      world.y - this.gridConfig.tileHeight / 2,
      this.gridConfig.tileWidth,
      this.gridConfig.tileHeight
    );
  }

  destroy(): void {
    this.layers.forEach(container => container.destroy());
    this.layers.clear();
    this.scene = null;
    this.mainCamera = null;
  }
}

// Singleton instance
export const RenderSystem = new RenderSystemManager();
